import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {
  ChartDrilldownSelection,
  CoverageReport,
  CoverageReportChart,
  CoverageReportChartDrilldown,
  CoverageReportChartResponse,
  MyUserReport,
  SearchProps,
} from 'app/interfaces';
import {MyCoverageService, SearchFilterDefaultsService} from 'services/index';
import {ExportFilenameService} from 'app/components';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexStroke,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
} from 'ng-apexcharts';
import {Paginator} from 'primeng/paginator';
import {Table} from 'primeng/table';
import {AdvancedSearchComponent} from 'app/components/advanced-search/advanced-search.component';
import {Store} from '@ngrx/store';
import {ConfirmationService} from 'primeng/api';
import {Actions, ofType} from '@ngrx/effects';
import {AppState} from 'app/app.reducer';
import * as actions from 'redux/actions';
import {FacetSearch, Search} from 'app/models';
import {Subscription} from 'rxjs';
import {delay, distinctUntilKeyChanged, take} from 'rxjs/operators';
import {FILTERS} from 'app/constants';
import {eq, filter, find, isEmpty, join, toLower} from 'lodash';
import {ReportTableActions} from '../report-table-actions';
import {alignPieChartData} from './coverage-chart-data';

interface ChartOptions {
  series: ApexAxisChartSeries;
  nonAxisSeries: ApexNonAxisChartSeries;
  labels: string[];
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  grid: ApexGrid;
  legend: ApexLegend;
  colors: string[];
  title: ApexTitleSubtitle;
  subtitle: ApexTitleSubtitle;
  markers: ApexMarkers;
}

type StandardChartPreset = 'MY_COVERAGE' | 'SHARE_OF_VOICE' | 'MY_COMPETITORS';
const CHART_COLORS = ['#5B5F66', '#C44E96', '#59A14F', '#E15759', '#B07AA1', '#76B7B2', '#EDC948', '#9C755F'];
const AXIS_LABEL_COLOR = '#373d3f';
const SELECTED_INTERVAL_LABEL_COLOR = '#f59e0b';
const AXIS_LABEL_FONT_SIZE = '12px';
const SELECTED_INTERVAL_LABEL_FONT_SIZE = '14px';

interface SelectedChartInterval {
  dataPointIndex: number;
  label: string;
}

@Component({
  selector: 'app-coverage-tab',
  templateUrl: './coverage-tab.component.html',
  styleUrls: ['../my-coverage-reporting.component.scss'],
})
export class CoverageTabComponent extends ReportTableActions implements OnInit, OnChanges, OnDestroy {
  public chartOptions: Partial<ChartOptions>;
  public pieChartOptions: Partial<ChartOptions>;
  public coverageData: CoverageReport[];
  public coverageReportChart: CoverageReportChart;
  public coverageDataChart: CoverageReportChartResponse;
  public coverageReportRequest: SearchProps;
  public loading: boolean;
  public loadingChart: boolean;
  public pageSize: number;
  public page: number;
  public totalItems: number;
  public totalPages: number;
  public currentPage: number;
  public searchBody: Search;
  public chartFiltersForm: FormGroup;
  public chartSettingsForm: FormGroup;
  private sidebarSub: Subscription;
  private topicsSub: Subscription;
  private selectedReportSub: Subscription;
  private reportsListSub: Subscription;
  private deleteReportSub: Subscription;
  private resetReportsSub: Subscription;
  private activeReportId: number | null = null;
  public savedReports: any[] = [];
  public showSaveReportDialog = false;
  public saveReportTitle = '';
  private initializeReportOnLoad = false;
  private _lastChartDataType = 'ARTICLES';

  public topicOptions: { label: string; value: number }[];
  public competitorOptions: { label: string; value: number }[];
  public outletGroupOptions: { label: string; value: number }[];
  public myCompanyQuery: string;
  private validUserTopicIds: Set<number>;

  public showChartSettings: boolean;
  public showDataFilters: boolean;
  public activeStandardChartPreset: StandardChartPreset | null;
  public savingAuthorId: number | null = null;
  public savingOutletId: number | null = null;
  public savingArticleId: number | null = null;
  private savedAuthors: any[] = [];
  private savedOutlets: any[] = [];
  private savedContentItems: any[] = [];
  private authorsOutletsSub: Subscription;
  private contentItemsSub: Subscription;
  public standardChartTypeOptions: { label: string; value: StandardChartPreset }[];
  public savedChartPlaceholders: string[];
  private applyingPreset: boolean;
  private chartSettingsSnapshot: any;
  private chartSettingsApplied: boolean;
  private lastChartDevicePixelRatio: number;

  public timeframeOptions = [
    { label: 'Past 1 month', value: 'ONE_MONTH' },
    { label: 'Past 3 months', value: 'THREE_MONTHS' },
    { label: 'Past 1 year', value: 'ONE_YEAR' },
    { label: 'Past 2 years', value: 'TWO_YEARS' },
    { label: 'Past 5 years', value: 'FIVE_YEARS' },
    { label: 'Past 10 years', value: 'TEN_YEARS' },
    { label: 'Custom', value: 'CUSTOM' },
  ];

  public intervalOptions = [
    { label: 'Year', value: 'ONE_YEAR' },
    { label: 'Month', value: 'ONE_MONTH' },
  ];

  public dataTypeOptions = [
    { label: 'Articles', value: 'ARTICLES' },
    { label: 'Authors', value: 'AUTHORS' },
    { label: 'Outlets', value: 'OUTLETS' },
  ];

  public chartTypeOptions = [
    { label: 'Bar', value: 'BAR' },
    { label: 'Line', value: 'LINE' },
    { label: 'Area', value: 'AREA' },
  ];

  @Input() projectId: number;
  @Input() projectName: string;
  @Input() projectColors: string;
  @ViewChild('paginator', { static: false }) paginator: Paginator;
  @ViewChild('coverageTable', { static: false }) table: Table;
  @ViewChild('reportSearchFilters', { static: false }) reportSearchFilters: AdvancedSearchComponent;

  public showDateRangeDialog: boolean;
  public editFromDate: Date;
  public editToDate: Date;
  public activeSearchFilters: Search | null;
  public chartClickFilter: { topicName: string; totalArticles: number; keywords?: string } | null;
  public showCoverageTable: boolean;
  private selectedChartInterval: SelectedChartInterval | null;

  private readonly chartViewportResizeHandler = () => this.scheduleChartZoomRefresh();

  constructor(
    private myCoverageService: MyCoverageService,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private actions$: Actions,
    private confirmationService: ConfirmationService,
    private searchFilterDefaults: SearchFilterDefaultsService,
    exportFilenameService: ExportFilenameService
  ) {
    super(myCoverageService, store, exportFilenameService);
    this.topicOptions = [{ label: '(None)', value: null }];
    this.competitorOptions = [];
    this.chartFiltersForm = this.createChartFiltersForm();
    this.chartSettingsForm = this.createChartFiltersForm();
    this.coverageReportChart = {
      dataType: 'ARTICLES',
      timeframe: 'ONE_YEAR',
      interval: 'ONE_MONTH',
      compareToPrior: false,
      domainTopic: null,
      myCompanyTopic: null,
      competitorTopics: [],
      includeOther: false,
      calendarYear: true,
      forecast: false,
    };
    this.coverageReportRequest = {
      timeframe: 'ONE_YEAR',
      ...this.buildSearchFilterFields(null),
    };
    this.showChartSettings = false;
    this.showDataFilters = false;
    this.activeStandardChartPreset = null;
    this.standardChartTypeOptions = [
      { label: 'My Coverage', value: 'MY_COVERAGE' },
      { label: 'Share of Voice', value: 'SHARE_OF_VOICE' },
      { label: 'My Competitors', value: 'MY_COMPETITORS' },
    ];
    this.applyingPreset = false;
    this.coverageDataChart = null;
    this.coverageData = [];
    this.loading = false;
    this.loadingChart = false;
    this.showDateRangeDialog = false;
    this.editFromDate = null;
    this.editToDate = null;
    this.activeSearchFilters = null;
    this.chartClickFilter = null;
    this.showCoverageTable = false;
    this.selectedChartInterval = null;
    this.pageSize = 25;
    this.page = 1;
    this.totalItems = 0;
    this.totalPages = 0;
    this.currentPage = 1;
    this.lastChartDevicePixelRatio = window.devicePixelRatio || 1;
    this.outletGroupOptions = [];
  }

  private createChartFiltersForm(): FormGroup {
    return this.fb.group({
      title: [''],
      timeframe: ['ONE_YEAR'],
      interval: ['ONE_MONTH'],
      dataType: ['ARTICLES'],
      chartType: ['BAR'],
      showPieChart: [false],
      calendarYear: [true],
      forecast: [false],
      compareToPrior: [false],
      includeOther: [true],
      displayNames: [true],
      displayValues: [true],
      hideZeroValues: [false],
      showIntersections: [false],
      domainTopic: [null],
      competitorTopics: [[]],
      outletGroups: [[]],
      fromDate: [null],
      toDate: [null],
    });
  }

  private syncChartSettingsFromMainForm() {
    this.chartSettingsForm.reset(this.chartFiltersForm.getRawValue(), { emitEvent: false });
  }

  private syncMainFormFromChartSettings() {
    this.chartFiltersForm.reset(this.chartSettingsForm.getRawValue(), { emitEvent: false });
  }

  onChartSettingsFilterChange() {
    this.syncMainFormFromChartSettings();
    this.onChartFilterChange();
  }

  onChartSettingsTypeChange() {
    this.syncMainFormFromChartSettings();
    this.onChartTypeChange();
    this.syncChartSettingsFromMainForm();
  }

  onChartSettingsDisplayChange() {
    this.syncMainFormFromChartSettings();
    this.onDisplayValuesChange();
  }

  onChartSettingsTitleBlur() {
    this.syncMainFormFromChartSettings();
    this.onTitleBlur();
  }

  get isAuthorsDataType(): boolean {
    return this.chartFiltersForm?.get('dataType')?.value === 'AUTHORS';
  }

  get isOutletsDataType(): boolean {
    return this.chartFiltersForm?.get('dataType')?.value === 'OUTLETS';
  }

  get isShowPieChart(): boolean {
    return this.chartFiltersForm?.get('showPieChart')?.value;
  }

  get chartHeight(): number {
    return this.showCoverageTable ? 220 : 720;
  }

  ngOnInit() {
    this.store.select('advancedSearch').subscribe(({ outletGroups, onResetFilters }) => {
      if (outletGroups?.length > 0) {
        const existingFilter = FILTERS.find((f) => f.key === 'outletGroups');
        const mappedGroups = outletGroups.map((group) => ({
          label: group.name,
          value: group.id.toString(),
        }));
        if (existingFilter) {
          existingFilter.filters = mappedGroups;
        } else {
          FILTERS.push({ key: 'outletGroups', filters: mappedGroups });
        }
      }
      if (onResetFilters) {
        this.collapseCoverageTable();
      }
    });

    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody) {
        this.searchBody = searchBody;
      }
    });

    this.sidebarSub = this.store
      .select('ui')
      .pipe(distinctUntilKeyChanged('visible'))
      .subscribe(() => {
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 400);
      });

    this.topicsSub = this.store
      .select('topics')
      .pipe(distinctUntilKeyChanged('topics'))
      .subscribe(({ topics }) => {
        this.validUserTopicIds = new Set(
          (topics || [])
            .map((topic) => this.coerceSavedTopicId(topic?.userTopicId))
            .filter((topicId): topicId is number => topicId !== null)
        );
        this.topicOptions = [
          { label: '(None)', value: null },
          // Only list topics which have not been labeled as something else (e.g. 'My Company', 'Competitor')
          ...filter(topics, (t) => !t.categoryName).map((t) => ({ label: t.name, value: t.userTopicId })),
        ];
        const competitorTopics = filter(topics, (t) => eq(t.categoryName, 'Competitor'));
        this.competitorOptions = competitorTopics.map((t) => ({ label: t.name, value: t.userTopicId }));
        const myCompany = find(topics, (t) => eq(t.categoryName, 'My Company'));
        if (myCompany) {
          this.myCompanyQuery = myCompany.query ? myCompany.query : `"${myCompany.name}"`;
        } else {
          this.myCompanyQuery = null;
        }
      });

    this.reportsListSub = this.store
      .select('myReportsList')
      .subscribe(({ reports, loaded }) => {
        this.savedReports = reports || [];
        if (loaded && this.initializeReportOnLoad) {
          this.initializeReportOnLoad = false;
          this.initializeDefaultReport(this.savedReports);
        }
      });

    this.selectedReportSub = this.store
      .select('myReportsList')
      .pipe(distinctUntilKeyChanged('selectedReport'))
      .subscribe(({ selectedReport }) => {
        if (selectedReport) {
          this.activeReportId = selectedReport.id;
          this.applyReportConfig(selectedReport);
        }
      });

    this.deleteReportSub = this.actions$
      .pipe(ofType(actions.deleteMyReportSuccess))
      .subscribe(({ reportId }) => {
        if (this.activeReportId && eq(this.activeReportId, reportId)) {
          this.activeReportId = null;
          this.applyStandardChartPreset('MY_COVERAGE');
        }
      });

    this.resetReportsSub = this.store
      .select('myReportsList')
      // Reset actions can be dispatched while Angular is checking this view.
      // Defer the reset so savedReports does not change during that same pass.
      .pipe(distinctUntilKeyChanged('onReset'), delay(0))
      .subscribe(({ onReset, reports }) => {
        if (onReset) {
          this.resetProjectReportState();
          this.initializeDefaultReport(reports || []);
          this.store.dispatch(actions.resetMyReportsList({ onReset: false }));
        }
      });

    this.authorsOutletsSub = this.store
      .select('authorsOutletsList')
      .subscribe(({ authors, outlets, isAddingAuthorOutlet, isDeletingAuthorOutlet }) => {
        this.savedAuthors = authors || [];
        this.savedOutlets = outlets || [];
        if (!isAddingAuthorOutlet && !isDeletingAuthorOutlet) {
          this.savingAuthorId = null;
          this.savingOutletId = null;
        }
      });

    this.contentItemsSub = this.store
      .select('contentItemsList')
      .subscribe(({ contentItems, isAddingContentItem, isDeletingContentItem }) => {
        this.savedContentItems = contentItems || [];
        if (!isAddingContentItem && !isDeletingContentItem) {
          this.savingArticleId = null;
        }
      });

    this.registerChartViewportListeners();
  }

  ngOnDestroy() {
    this.unregisterChartViewportListeners();
    if (this.sidebarSub) { this.sidebarSub.unsubscribe(); }
    if (this.topicsSub) { this.topicsSub.unsubscribe(); }
    if (this.selectedReportSub) { this.selectedReportSub.unsubscribe(); }
    if (this.reportsListSub) { this.reportsListSub.unsubscribe(); }
    if (this.deleteReportSub) { this.deleteReportSub.unsubscribe(); }
    if (this.resetReportsSub) { this.resetReportsSub.unsubscribe(); }
    if (this.authorsOutletsSub) { this.authorsOutletsSub.unsubscribe(); }
    if (this.contentItemsSub) { this.contentItemsSub.unsubscribe(); }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.projectId && changes.projectId.currentValue) {
      this.resetProjectReportState();
      this.initializeReportOnLoad = true;
      const projectId = changes.projectId.currentValue;
      this.store.select('myReportsList').pipe(take(1)).subscribe((state) => {
        if (state.projectId !== projectId || (!state.loaded && !state.loading) || state.errors) {
          this.store.dispatch(actions.loadMyReportsList({ projectId }));
        }
      });
    }
  }

  onChartFilterChange() {
    if (!this.applyingPreset) {
      this.activeStandardChartPreset = null;
    }
    const { timeframe, interval, dataType, calendarYear, forecast, compareToPrior,
            includeOther, domainTopic, competitorTopics, showIntersections, fromDate, toDate } = this.chartFiltersForm.value;
    const customFromDate = timeframe === 'CUSTOM' ? fromDate : null;
    const customToDate = timeframe === 'CUSTOM' ? toDate : null;
    const normalizedCompetitorTopics = this.sanitizeSavedTopicIds(competitorTopics);

    this._lastChartDataType = dataType;
    this.coverageReportChart = {
      ...this.coverageReportChart,
      timeframe,
      interval,
      dataType,
      calendarYear,
      forecast,
      compareToPrior,
      competitorTopics: normalizedCompetitorTopics,
      domainTopic,
      includeOther: domainTopic ? includeOther : false,
      showIntersections,
      from: customFromDate,
      to: customToDate,
    };
    this.coverageReportRequest = {
      ...this.coverageReportRequest,
      ...this.getActiveSearchFilterFields(),
      timeframe,
      from: customFromDate,
      to: customToDate,
      keywords: null,
    };

    // While the Report Settings dialog is open, changes are staged only.
    // The chart is refreshed once when the user clicks Apply.
    if (this.showChartSettings) {
      return;
    }

    this.getCoverageDataChart();

    this.collapseCoverageTable();
    // this.coverageReportRequest = { ...this.coverageReportRequest, timeframe };
    // this.page = 1;
    // this.getCoverageData();
  }

  onChartDateClear(controlName: 'fromDate' | 'toDate') {
    this.chartFiltersForm.get(controlName).setValue(null, { emitEvent: false });
    this.onChartFilterChange();
  }

  onChartDateInput(controlName: 'fromDate' | 'toDate', event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.value) { return; }

    this.onChartDateClear(controlName);
  }

  onChartSettingsDateClear(controlName: 'fromDate' | 'toDate') {
    this.chartSettingsForm.get(controlName).setValue(null, { emitEvent: false });
    this.onChartSettingsFilterChange();
  }

  onChartSettingsDateInput(controlName: 'fromDate' | 'toDate', event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.value) { return; }

    this.onChartSettingsDateClear(controlName);
  }

  onChartTypeChange() {
    if (!this.applyingPreset) {
      this.activeStandardChartPreset = null;
    }
    // Reset any chart-click filter when switching chart types (pie ↔ bar/line/etc.)
    this.chartClickFilter = null;
    this.clearSelectedChartInterval();

    // If the request had a CUSTOM date range from a bar-chart click, restore the form's timeframe
    const formTimeframe = this.chartFiltersForm.get('timeframe').value;
    const effectiveTimeframe = formTimeframe === 'CUSTOM' ? 'ONE_YEAR' : formTimeframe;
    if (formTimeframe === 'CUSTOM') {
      this.chartFiltersForm.patchValue({ timeframe: 'ONE_YEAR' }, { emitEvent: false });
    }
    this.coverageReportRequest = {
      ...this.coverageReportRequest,
      timeframe: effectiveTimeframe,
      keywords: null,
      from: null,
      to: null,
    };

    this.chartFiltersForm.get('interval').patchValue('ONE_MONTH', { emitEvent: false });
    this.coverageReportChart = { ...this.coverageReportChart, interval: 'ONE_MONTH' };
    // Stage chart-type changes while the dialog is open; commit on Apply.
    if (this.showChartSettings) {
      return;
    }
    if (this.chartOptions) {
      this.initializeChartOptions();
    }
    this.getCoverageDataChart();
  }

  onDisplayValuesChange() {
    if (!this.applyingPreset) {
      this.activeStandardChartPreset = null;
    }
    // Stage display changes while the dialog is open; commit on Apply.
    if (this.showChartSettings) {
      return;
    }
    if (this.chartOptions) {
      this.initializeChartOptions();
    }
  }

  onTitleBlur() {
    if (this.showChartSettings) {
      return;
    }
    if (this.chartOptions) {
      this.initializeChartOptions();
    }
  }

  onChartSettingsHide() {
    this.showChartSettings = false;
    this.showDataFilters = false;
    if (this.chartSettingsApplied) {
      // Commit the staged settings and refresh the chart once.
      this.chartClickFilter = null;
      this.collapseCoverageTable();
      if (this.chartOptions) {
        this.initializeChartOptions();
      }
      this.getCoverageDataChart();
    } else {
      // Dismissed without applying (Cancel or mask/close): discard staged
      // changes by restoring the snapshot taken on open. The live chart was
      // never modified, so no refresh is required.
      this.restoreChartSettingsSnapshot();
    }
    this.chartSettingsApplied = false;
  }

  toggleChartSettings() {
    if (this.showChartSettings) {
      this.onChartSettingsCancel();
      return;
    }
    this.chartSettingsApplied = false;
    this.snapshotChartSettings();
    this.syncChartSettingsFromMainForm();
    this.reportSearchFilters?.syncSearchFilters(this.activeSearchFilters);
    this.showDataFilters = false;
    this.showChartSettings = true;
  }

  createStandardReport(preset: StandardChartPreset) {
    this.applyStandardChartPreset(preset);
  }

  selectSavedReport(report: any) {
    this.store.dispatch(actions.selectMyReport({ report }));
  }

  createCustomReport() {
    this.chartSettingsApplied = false;
    this.snapshotChartSettings();
    this.stageNewReportDefaults();
    this.syncChartSettingsFromMainForm();
    this.reportSearchFilters?.syncSearchFilters(this.activeSearchFilters);
    this.showDataFilters = false;
    this.showChartSettings = true;
  }

  toggleDataFilters() {
    this.showDataFilters = !this.showDataFilters;
  }

  private snapshotChartSettings() {
    this.chartSettingsSnapshot = {
      form: this.chartFiltersForm.getRawValue(),
      coverageReportChart: { ...this.coverageReportChart },
      coverageReportRequest: { ...this.coverageReportRequest },
      activeStandardChartPreset: this.activeStandardChartPreset,
      activeReportId: this.activeReportId,
      activeSearchFilters: this.activeSearchFilters,
      searchBody: this.searchBody,
    };
  }

  onChartSettingsApply() {
    this.syncMainFormFromChartSettings();
    this.reportSearchFilters?.applyEmbeddedFilters();
    this.onChartFilterChange();
    // Mark the settings as applied; the actual commit + chart refresh happens
    // once in onChartSettingsHide when the dialog closes.
    this.chartSettingsApplied = true;
    this.showChartSettings = false;
    this.showDataFilters = false;
  }

  onChartSettingsCancel() {
    // Discard the staged changes; onChartSettingsHide restores the snapshot.
    this.chartSettingsApplied = false;
    this.showChartSettings = false;
  }

  private restoreChartSettingsSnapshot() {
    if (this.chartSettingsSnapshot) {
      this.chartFiltersForm.reset(this.chartSettingsSnapshot.form, { emitEvent: false });
      this.coverageReportChart = { ...this.chartSettingsSnapshot.coverageReportChart };
      this.coverageReportRequest = { ...this.chartSettingsSnapshot.coverageReportRequest };
      this.activeStandardChartPreset = this.chartSettingsSnapshot.activeStandardChartPreset;
      this.activeReportId = this.chartSettingsSnapshot.activeReportId;
      this.activeSearchFilters = this.chartSettingsSnapshot.activeSearchFilters;
      this.searchBody = this.chartSettingsSnapshot.searchBody;
    }
  }

  private stageNewReportDefaults() {
    const emptyFilters = this.buildSearchFilterFields(null);
    this.activeReportId = null;
    this.activeStandardChartPreset = null;
    this.activeSearchFilters = null;
    this.chartFiltersForm.patchValue({ outletGroups: [] }, { emitEvent: false });
    this.chartClickFilter = null;
    this.clearSelectedChartInterval();
    this.chartFiltersForm.reset({
      ...this.getDefaultChartFilterValues(),
      title: this.projectName || '',
    }, { emitEvent: false });
    this.coverageReportChart = {
      ...this.getDefaultCoverageReportChart(),
      ...emptyFilters,
      keywords: null,
      from: null,
      to: null,
    };
    this.coverageReportRequest = {
      timeframe: 'ONE_YEAR',
      ...emptyFilters,
      keywords: null,
      from: null,
      to: null,
    };
    this.page = 1;
  }

  applyStandardChartPreset(preset: StandardChartPreset) {
    const presetValues = this.getStandardChartPresetValues(preset);
    if (!presetValues) { return; }

    this.applyingPreset = true;
    this.activeStandardChartPreset = preset;
    this.collapseCoverageTable();
    this.chartClickFilter = null;
    this.editFromDate = null;
    this.editToDate = null;

    this.chartFiltersForm.reset(this.getDefaultChartFilterValues(), { emitEvent: false });
    this.chartFiltersForm.patchValue({
      ...presetValues,
      outletGroups: this.activeSearchFilters?.outletGroups || [],
    }, { emitEvent: false });

    const { timeframe, interval, dataType, calendarYear, forecast, compareToPrior, includeOther, domainTopic, competitorTopics } =
      this.chartFiltersForm.value;
    const normalizedCompetitorTopics = this.sanitizeSavedTopicIds(competitorTopics);
    this._lastChartDataType = dataType;
    this.coverageReportChart = {
      ...this.coverageReportChart,
      timeframe,
      interval,
      dataType,
      calendarYear,
      forecast,
      compareToPrior,
      competitorTopics: normalizedCompetitorTopics,
      domainTopic,
      includeOther: domainTopic ? includeOther : false,
      from: null,
      to: null,
      keywords: null,
    };
    this.coverageReportRequest = {
      ...this.coverageReportRequest,
      timeframe,
      keywords: null,
      from: null,
      to: null,
    };
    this.page = 1;

    if (this.chartOptions) {
      this.initializeChartOptions();
    }
    this.getCoverageDataChart();
    this.getCoverageData();
    this.applyingPreset = false;
  }

  initializeChartOptions() {
    const { title, chartType, dataType, timeframe, displayNames, displayValues, hideZeroValues } = this.chartFiltersForm.value;
    const chartTitle = title || this.coverageDataChart?.title;
    const pieChartData = this.getSelectedPieChartData();
    const chartCategories = this.coverageDataChart?.periods.map((p) => p.label) || [];
    const formatPieDataLabel = (rawValue: number, percentage: number) => {
      const formattedValue = Number(rawValue || 0).toLocaleString();
      return `${formattedValue} (${percentage.toFixed(0)}%)`;
    };
    const getPiePercentage = (series: number[], seriesIndex: number) => {
      const rawValue = Number(series?.[seriesIndex] || 0);
      const total = (series || []).reduce((sum, value) => sum + Number(value || 0), 0);
      return total ? (rawValue / total) * 100 : 0;
    };
    let colors: string[];
    if (this.projectColors) {
      colors = this.projectColors.split(',').map((color) => color.trim());
      colors = [...colors, ...CHART_COLORS.slice(1)]; // Skip the first default color, the rest will come after any custom colors
    } else {
      colors = CHART_COLORS;
    }

    this.chartOptions = {
      title: {
        text: chartTitle,
        align: 'left',
      },
      subtitle: {
        text: this.timeframeOptions.find(t => t.value === timeframe).label + ' - Number of '
          + this.dataTypeOptions.find(d => d.value === dataType).label,
        align: 'left',
      },
      series: this.coverageDataChart?.series || [],
      xaxis: {
        categories: chartCategories,
        type: 'category',
        labels: {
          style: {
            colors: this.getXAxisLabelColors(chartCategories),
            fontSize: AXIS_LABEL_FONT_SIZE,
          },
        },
      },
      chart: {
        type:
          chartType === 'LINE'
            ? 'line'
            : chartType.endsWith('AREA')
            ? 'area'
            : 'bar',
        stacked: chartType.startsWith('STACKED') || chartType === 'PERCENTAGE',
        stackType: chartType === 'PERCENTAGE' ? '100%' : 'normal',
        height: this.chartHeight,
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        toolbar: {
          show: true,
          tools: {
            download: '<i class="pi pi-download" style="font-size: 1.5rem"></i>',
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
          export: {
            png: { filename: chartTitle },
            svg: { filename: chartTitle },
            csv: { filename: chartTitle },
          }
        },
        events: {
          mounted: (chartContext) => {
            this.applySelectedAxisLabelStyle(chartContext);
          },
          updated: (chartContext) => {
            this.applySelectedAxisLabelStyle(chartContext);
          },
          click: (_event, _chartContext, opts) => {
            this.handleChartIntervalSelection(opts?.seriesIndex, opts?.dataPointIndex);
          },
        },
      },
      colors: colors,
      stroke: { curve: chartType === 'LINE' ? 'straight' : 'smooth', width: 3 },
      markers: { size: 4 },
      dataLabels: {
        offsetY: -20,
        style: {
          colors: ['#373d3f'],
          fontSize: '12px',
          fontWeight: 600,
        },
        background: {
          enabled: false,
        },
        formatter: (val: number, opts: any) => {
          const name = opts?.w?.config?.series?.[opts.seriesIndex]?.name || '';
          if (displayValues) {
            if (!hideZeroValues || val > 0) {
              return `${Number(val).toLocaleString()}`;
            } else {
              return '';
            }
          } else if (displayNames) {
            return name;
          }
        },
      },
      plotOptions: {
        bar: {
          dataLabels: {
            position: 'top',
            hideOverflowingLabels: false
          },
        },
      },
      grid: {
        borderColor: '#e7e7e7',
        padding: {
          top: 20
        }
      },
      legend: {
        show: true,
        // markers: { width: 12, height: 12 },
        position: 'bottom',
        horizontalAlign: 'left'
      },
      tooltip: { enabled: true, onDatasetHover: { highlightDataSeries: true } },
    };

    this.pieChartOptions = {
      colors: colors,
      stroke: { curve: 'straight', width: 3 },
      markers: { size: 4 },
      dataLabels: {
        formatter: (val: number, opts: any) => {
          const name = opts?.w?.globals?.labels?.[opts.seriesIndex] || '';
          const rawValue = opts?.w?.globals?.series?.[opts.seriesIndex] || 0;
          if (displayNames && displayValues) {
            return `${name}: ${rawValue.toLocaleString()} (${val.toFixed(0)}%)`;
          } else if (displayValues) {
            if (!hideZeroValues || val > 0) {
              return `${rawValue.toLocaleString()} (${val.toFixed(0)}%)`;
            } else {
              return '';
            }
          } else if (displayNames) {
            return name;
          }
        },
        style: {
          fontSize: '13px',
          fontWeight: 600,
          colors: ['#fff'],
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          color: '#000',
          opacity: 0.8,
        },
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        showForNullSeries: true,
        showForZeroSeries: true,
        formatter: (seriesName: string, opts: any) => {
          const series = opts?.w?.globals?.series || [];
          const rawValue = series[opts.seriesIndex];
          const percentage = getPiePercentage(series, opts.seriesIndex);
          return `${seriesName}: ${formatPieDataLabel(rawValue, percentage)}`;
        },
        // markers: { width: 12, height: 12 },
        // itemMargin: { vertical: 40 },
        position: 'bottom',
        horizontalAlign: 'left',
        offsetY: -20,
        fontSize: '13px',
      },
      plotOptions: {
        pie: {
          customScale: 0.78,
          offsetY: -25,
          dataLabels: {
            offset: -8,
            minAngleToShowLabel: 18,
          },
        },
      },
      tooltip: { enabled: true },
      title: {
        text: chartTitle,
        align: 'left',
      },
      subtitle: {
        text: pieChartData.periodLabel + ' - Number of ' + this.dataTypeOptions.find(d => d.value === dataType).label,
        align: 'left',
      },
      labels: [...pieChartData.labels],
      nonAxisSeries: pieChartData.series,
      chart: {
        type: 'pie',
        height: this.chartHeight,
        width: '100%',
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        toolbar: {
          show: true,
          tools: {
            download: '<i class="pi pi-download" style="font-size: 1.5rem"></i>',
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
          export: {
            png: { filename: chartTitle },
            svg: { filename: chartTitle },
            csv: { filename: chartTitle },
          }
        },
        events: {
          mounted: (chartContext) => {
            this.positionPieLegend(chartContext);
          },
          updated: (chartContext) => {
            this.positionPieLegend(chartContext);
          },
          dataPointSelection: (_event, _chartContext, opts) => {
            const dataPointIndex: number = opts.dataPointIndex;
            if (dataPointIndex < 0) { return; }
            const currentPieChartData = this.getSelectedPieChartData();
            const topicName = currentPieChartData.labels?.[dataPointIndex] || '';
            const articleCount = currentPieChartData.series?.[dataPointIndex] ?? 0;
            const keywords = currentPieChartData.queries?.[dataPointIndex] || '';
            const drilldown = currentPieChartData.drilldowns?.[dataPointIndex]
              || this.getEntityDrilldownSelection(dataPointIndex);
            const from = currentPieChartData.from || '';
            const to = currentPieChartData.to || '';
            this.onSovSliceClicked({ topicName, articleCount, keywords, from, to, drilldown });
          },
        },
      }
    };
  }

  /* For non-axis charts, ApexCharts recalculates the pie canvas height after rendering.
  The bottom legend is absolutely positioned using the original svgHeight, but the pie’s resized canvas can be shorter.
  The legend consequently lands below the canvas and gets clipped.
  A top legend would work because its position is calculated from the title/subtitle rather than the canvas bottom.
  But in order to get the bottom legend to work, we need to adjust the legend’s position after the chart has been rendered.
   */
  private positionPieLegend(chartContext: any) {
    const legend = chartContext?.el?.querySelector?.(
      '.apexcharts-legend.apx-legend-position-bottom'
    ) as HTMLElement;

    if (!legend) { return; }

    legend.style.setProperty('top', 'auto', 'important');
    legend.style.setProperty('bottom', '10px', 'important');
  }

  private getSelectedPieChartData():
    { periodLabel: string; from: string; to: string; labels: string[]; queries: string[]; series: number[];
      drilldowns: Array<ChartDrilldownSelection | undefined> } {
    const periods = this.coverageDataChart?.periods || [];
    const lastPeriodIndex = periods.length - 1;
    const selectedPeriodIndex = this.selectedChartInterval?.dataPointIndex ?? lastPeriodIndex;
    const periodIndex = selectedPeriodIndex >= 0 && selectedPeriodIndex < periods.length ? selectedPeriodIndex : lastPeriodIndex;
    const period = periods[periodIndex];
    const pie = this.coverageDataChart?.pie;

    // The API's pie payload contains the mutually-exclusive buckets used by
    // "Display intersections" (including their exact drilldown queries). The
    // timeline series only contains the individual topics, so rebuilding the
    // latest pie from those series drops every intersection slice.
    if (pie && periodIndex === lastPeriodIndex) {
      const alignedPie = alignPieChartData(pie, this.coverageDataChart?.series || []);
      return {
        periodLabel: pie.title || period?.label || '',
        from: pie.from || period?.from || '',
        to: pie.to || period?.to || '',
        ...alignedPie,
      };
    }

    if (period) {
      return {
        periodLabel: period.label || '',
        from: period.from || '',
        to: period.to || '',
        labels: (this.coverageDataChart?.series || []).map((series) => series.name),
        queries: (this.coverageDataChart?.series || []).map((series) => series.query || ''),
        series: (this.coverageDataChart?.series || []).map((series) => Number(series.data?.[periodIndex] || 0)),
        drilldowns: (this.coverageDataChart?.series || []).map((series) => series.drilldown),
      };
    }

    return {
      periodLabel: pie?.title || '',
      from: pie?.from || '',
      to: pie?.to || '',
      labels: pie?.labels || [],
      queries: pie?.queries || [],
      series: pie?.series || [],
      drilldowns: pie?.drilldowns || [],
    };
  }

  private getXAxisLabelColors(categories: string[]): string[] {
    const selectedIndex = this.showCoverageTable ? this.selectedChartInterval?.dataPointIndex : null;
    return categories.map((_category, index) => index === selectedIndex ? SELECTED_INTERVAL_LABEL_COLOR : AXIS_LABEL_COLOR);
  }

  private applySelectedAxisLabelStyle(chartContext: any) {
    setTimeout(() => {
      const labels = chartContext?.el?.querySelectorAll?.('.apexcharts-xaxis-texts-g text');
      if (!labels) { return; }
      const selectedIndex = this.showCoverageTable ? this.selectedChartInterval?.dataPointIndex : null;
      Array.prototype.forEach.call(labels, (label: SVGTextElement, index: number) => {
        const selected = index === selectedIndex;
        label.style.fontWeight = selected ? '700' : '400';
        label.style.fontSize = selected ? SELECTED_INTERVAL_LABEL_FONT_SIZE : AXIS_LABEL_FONT_SIZE;
        label.setAttribute('font-weight', selected ? '700' : '400');
        label.setAttribute('font-size', selected ? SELECTED_INTERVAL_LABEL_FONT_SIZE : AXIS_LABEL_FONT_SIZE);
        label.setAttribute('fill', selected ? SELECTED_INTERVAL_LABEL_COLOR : AXIS_LABEL_COLOR);
      });
    });
  }

  private registerChartViewportListeners() {
    window.addEventListener('resize', this.chartViewportResizeHandler);
  }

  private unregisterChartViewportListeners() {
    window.removeEventListener('resize', this.chartViewportResizeHandler);
  }

  private scheduleChartZoomRefresh() {
    const currentDevicePixelRatio = window.devicePixelRatio || 1;
    if (currentDevicePixelRatio === this.lastChartDevicePixelRatio) {
      return;
    }

    this.lastChartDevicePixelRatio = currentDevicePixelRatio;
    if (!this.coverageDataChart || this.loadingChart) { return; }

    this.initializeChartOptions();
  }

  private setSelectedChartInterval(dataPointIndex: number) {
    const label = this.coverageDataChart?.periods?.[dataPointIndex]?.label;
    this.selectedChartInterval = label ? { dataPointIndex, label } : null;
  }

  private clearSelectedChartInterval() {
    this.selectedChartInterval = null;
  }

  getCoverageData() {
    if (!this.projectId || !this.showCoverageTable) { return; }
    this.loading = true;
    this.myCoverageService
      .getCoverageReport(this.projectId, this.page - 1, this.pageSize, this.coverageReportRequest)
      .subscribe(
        (response: {
          content: CoverageReport[];
          totalElements: number;
          totalPages: number;
          size: number;
          number: number;
          numberOfElements: number;
        }) => {
          this.coverageData = response.content || [];
          this.totalItems = response.totalElements || 0;
          this.totalPages = response.totalPages || 0;
          this.pageSize = response.size || 50;
          this.currentPage = response.number || 0;
          this.loading = false;
          // Only backfill from API when the count wasn't already known (bar/line chart click).
          // SOV pie click sets the exact count directly from the chart — do not overwrite it.
          if (this.chartClickFilter && this.chartClickFilter.totalArticles === null) {
            this.chartClickFilter = { ...this.chartClickFilter, totalArticles: this.totalItems };
          }
        },
        (error) => {
          console.error('Error fetching coverage data:', error);
          this.coverageData = [];
          this.totalItems = 0;
          this.totalPages = 0;
          this.loading = false;
        }
      );
  }

  getCoverageDataChart() {
    if (!this.projectId) { return; }
    this.loadingChart = true;
    this.myCoverageService
      .getReportChart(this.projectId, this.coverageReportChart)
      .subscribe(
        (response: CoverageReportChartResponse) => {
          this.coverageDataChart = response;
          this.loadingChart = false;
          if (!this.chartFiltersForm.get('title').value) {
            this.chartFiltersForm.patchValue({ title: response.title });
          }
          this.initializeChartOptions();
        },
        (error) => {
          console.error('Error fetching coverage data chart:', error);
          this.coverageDataChart = null;
          this.loadingChart = false;
        }
      );
  }

  get reportName(): string {
    return this.chartFiltersForm?.get('title')?.value || this.coverageDataChart?.title || null;
  }

  protected getReportExportName(): string {
    return this.reportName;
  }

  onApplyDateRange(dateRange: { from: Date; to: Date | null }) {
    this.coverageReportRequest = {
      timeframe: 'CUSTOM',
      from: dateRange.from,
      to: dateRange.to,
    };
    this.editFromDate = null;
    this.editToDate = null;
    this.page = 1;
    this.getCoverageData();

    this.coverageReportChart = {
      ...this.coverageReportChart,
      timeframe: 'CUSTOM',
      from: dateRange.from,
      to: dateRange.to,
    };

    this.getCoverageDataChart();
  }

  private toLocalDate(dateStr: string): Date {
    const d = new Date(dateStr);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }

  onSovSliceClicked(event: { topicName: string; articleCount: number; keywords: string; from: string; to: string;
    drilldown?: ChartDrilldownSelection }) {
    if (!this.selectedChartInterval) {
      this.setSelectedChartInterval((this.coverageDataChart?.periods?.length || 0) - 1);
    }
    if (this.isAuthorsDataType || this.isOutletsDataType) {
      if (event.drilldown) {
        this.applyEntityChartDrilldown(
          event.drilldown,
          event.from,
          event.to,
          event.topicName,
          event.articleCount,
          event.keywords
        );
      }
      return;
    }
    this.coverageReportRequest = {
      ...this.coverageReportRequest,
      ...this.getActiveSearchFilterFields(),
      timeframe: 'CUSTOM',
      from: this.toLocalDate(event.from),
      to: this.toLocalDate(event.to),
      keywords: event.keywords,
    };
    this.chartClickFilter = {
      topicName: event.topicName,
      totalArticles: event.articleCount,
    };
    this.page = 1;
    this.expandCoverageTable();
    this.getCoverageData();
  }

  private expandCoverageTable() {
    this.showCoverageTable = true;
    if (this.chartOptions) {
      this.initializeChartOptions();
    }
  }

  private handleChartIntervalSelection(seriesIndex: number, dataPointIndex: number) {
    if (seriesIndex == null || dataPointIndex == null || seriesIndex < 0 || dataPointIndex < 0) { return; }
    this.setSelectedChartInterval(dataPointIndex);
    const period = this.coverageDataChart?.periods?.[dataPointIndex];
    const series = this.coverageDataChart?.series?.[seriesIndex];
    if (this.isAuthorsDataType || this.isOutletsDataType) {
      const drilldown = series?.drilldown || this.getEntityDrilldownSelection(seriesIndex);
      if (drilldown) {
        this.applyEntityChartDrilldown(
          drilldown,
          period?.from,
          period?.to,
          series.name,
          Number(series.data?.[dataPointIndex] || 0),
          series.query
        );
      }
      return;
    }
    this.coverageReportRequest = {
      ...this.getActiveSearchFilterFields(),
      timeframe: 'CUSTOM',
      from: this.toLocalDate(this.coverageDataChart?.periods?.[dataPointIndex]?.from),
      to: this.toLocalDate(this.coverageDataChart?.periods?.[dataPointIndex]?.to),
      keywords: this.coverageDataChart?.series?.[seriesIndex]?.query,
    };
    this.chartClickFilter = {
      topicName: this.coverageDataChart?.series?.[seriesIndex]?.name || '',
      totalArticles: null,
    };
    this.page = 1;
    this.expandCoverageTable();
    this.getCoverageData();
  }

  private applyEntityChartDrilldown(selection: ChartDrilldownSelection, periodFrom: string, periodTo: string,
                                    topicName: string, totalEntities: number, keywords?: string) {
    const drilldownRequest: CoverageReportChartDrilldown = {
      ...this.coverageReportChart,
      ...this.getActiveSearchFilterFields(),
      periodFrom,
      periodTo,
      selection,
    };
    this.coverageReportRequest = drilldownRequest;
    this.chartClickFilter = { topicName, totalArticles: totalEntities, keywords };
    this.page = 1;
    this.expandCoverageTable();
  }

  private getEntityDrilldownSelection(seriesIndex: number): ChartDrilldownSelection | undefined {
    if (!this.isAuthorsDataType && !this.isOutletsDataType) { return undefined; }
    const comparisonCount = 1 + (this.coverageReportChart?.competitorTopics?.length || 0);
    if (seriesIndex < comparisonCount) { return { type: 'COMPARISON', comparisonIndex: seriesIndex, subsetMembers: null }; }
    if (this.coverageReportChart?.includeOther && seriesIndex === comparisonCount) {
      return { type: 'OTHER', comparisonIndex: null, subsetMembers: null };
    }
    return undefined;
  }

  private withoutEntityChartDrilldown(): SearchProps {
    const { selection, periodFrom, periodTo, ...searchRequest } = this.coverageReportRequest as CoverageReportChartDrilldown;
    return searchRequest;
  }

  private collapseCoverageTable() {
    this.showCoverageTable = false;
    this.clearSelectedChartInterval();
    if (this.chartOptions) {
      this.initializeChartOptions();
    }
    this.loading = false;
    this.coverageData = [];
    this.totalItems = 0;
    this.totalPages = 0;
    this.currentPage = 1;
    this.page = 1;
  }

  clearCustomFilter() {
    this.chartClickFilter = null;
    this.clearSelectedChartInterval();
    const formTimeframe = this.chartFiltersForm.get('timeframe').value;
    const effectiveTimeframe = formTimeframe === 'CUSTOM' ? 'ONE_YEAR' : formTimeframe;
    if (formTimeframe === 'CUSTOM') {
      this.chartFiltersForm.patchValue({ timeframe: 'ONE_YEAR' }, { emitEvent: false });
    }
    this.coverageReportRequest = {
      ...this.withoutEntityChartDrilldown(),
      timeframe: effectiveTimeframe,
      keywords: null,
      from: null,
      to: null,
    };
    this.page = 1;
    if (this.chartOptions) {
      this.initializeChartOptions();
    }
    this.getCoverageData();
  }

  clearAllFilters() {
    this.chartClickFilter = null;
    this.clearSelectedChartInterval();
    this.activeSearchFilters = null;
    this.chartFiltersForm.patchValue({ outletGroups: [] }, { emitEvent: false });
    this.store.dispatch(actions.resetAdvancedSearchFilters({ onResetFilters: true }));
    const chartTimeframe = this.chartFiltersForm.get('timeframe')?.value || 'ONE_YEAR';
    const requestTimeframe = chartTimeframe === 'CUSTOM' ? 'ONE_YEAR' : chartTimeframe;
    const emptyFilters = this.buildSearchFilterFields(null);
    this.coverageReportRequest = {
      ...this.withoutEntityChartDrilldown(),
      timeframe: requestTimeframe,
      keywords: null,
      from: null,
      to: null,
      ...emptyFilters,
    };
    this.coverageReportChart = {
      ...this.coverageReportChart,
      ...emptyFilters,
    };
    this.page = 1;
    this.getCoverageData();
  }

  getPage(e: any) {
    this.currentPage = e.page;
    this.page = e.page + 1;
    this.getCoverageData();
    if (this.table) {
      setTimeout(() => { this.table.scrollTo({ top: 0 }); }, 300);
    }
  }

  getAuthorContent(authorItem) {
    const reportSearch = this.getReportContentSearch();
    const facetSearch = new FacetSearch({
      searchBody: new Search({ ...reportSearch, authors: [authorItem.authorId] }),
    });
    this.store.dispatch(
      actions.loadAuthorContent({
        facetSearch,
        author: { ...authorItem, id: authorItem.authorId, name: authorItem.author, outletNames: authorItem.outlet },
      })
    );
  }

  getOutletContent(outlet) {
    const reportSearch = this.getReportContentSearch();
    const facetSearch = new FacetSearch({
      searchBody: new Search({ ...reportSearch, outlets: [outlet.outletId] }),
    });
    this.store.dispatch(
      actions.loadOutletContent({
        facetSearch,
        outlet: { ...outlet, id: outlet.outletId, name: outlet.outlet },
      })
    );
  }

  goToTitleUrl(url) { window.open(url, '_blank'); }

  private getReportContentSearch(): SearchProps {
    const reportRequest: any = this.coverageReportRequest || {};
    const reportFrom = reportRequest.periodFrom || reportRequest.from;
    const reportTo = reportRequest.periodTo || reportRequest.to;

    return {
      ...this.searchBody,
      ...reportRequest,
      timeframe: reportFrom ? 'CUSTOM' : reportRequest.timeframe,
      from: reportFrom ? new Date(reportFrom) : null,
      to: reportTo ? new Date(reportTo) : null,
    };
  }

  onCoverageFilters(search: Search) {
    this.activeSearchFilters = this.hasSearchFilters(search) ? search : null;
    this.searchBody = search;
    this.chartFiltersForm.patchValue({ outletGroups: search?.outletGroups || [] }, { emitEvent: false });
    const filterFields = this.buildSearchFilterFields(search);
    this.chartClickFilter = null;
    this.clearSelectedChartInterval();

    this.coverageReportRequest = {
      ...this.withoutEntityChartDrilldown(),
      ...filterFields,
      keywords: null,
    };
    this.coverageReportChart = {
      ...this.coverageReportChart,
      ...filterFields,
      keywords: null,
    };
    this.page = 1;
    if (this.showChartSettings) {
      return;
    }
    if (this.chartOptions) {
      this.initializeChartOptions();
    }
    this.getCoverageData();
    this.getCoverageDataChart();
  }

  clearSearchFilters() {
    this.activeSearchFilters = null;
    this.chartFiltersForm.patchValue({ outletGroups: [] }, { emitEvent: false });
    this.store.dispatch(actions.resetAdvancedSearchFilters({ onResetFilters: true }));
    const emptyFilters = this.buildSearchFilterFields(null);
    this.coverageReportRequest = { ...this.coverageReportRequest, ...emptyFilters };
    this.coverageReportChart = { ...this.coverageReportChart, ...emptyFilters };
    this.page = 1;
    this.getCoverageData();
    this.getCoverageDataChart();
  }

  onQuickOutletGroupsChange(outletGroups: number[]) {
    const search = new Search({
      ...(this.activeSearchFilters || {}),
      outletGroups: outletGroups || [],
    });
    this.onCoverageFilters(search);
  }

  onOutletGroupOptionsChange(options: { label: string; value: number }[]) {
    this.outletGroupOptions = options || [];
  }

  get searchFiltersLabel(): string {
    if (!this.activeSearchFilters) { return ''; }
    const filterArr: string[] = [];
    const {
      analysts, countries, topAuthorsOnly, topOutletsOnly, outletGroups,
      authorFollowSelected, outletFollowSelected,
      authors, outlets, excludeAuthors, excludeOutlets,
      authorLabels, excludeAuthorLabels, outletLabels, excludeOutletLabels,
    } = this.activeSearchFilters;

    FILTERS.forEach((f: any) => {
      switch (f.key) {
        case 'outletGroups':
          if (outletGroups && outletGroups.length > 0) {
            const labels = outletGroups
              .map((groupId) => f.filters.find((item) => eq(item.value, groupId.toString())))
              .filter(Boolean)
              .map((item) => item.label);
            if (labels.length > 0) { filterArr.push(labels.join(', ')); }
          }
          break;
        case 'countries':
          if (countries) {
            const match = f.filters.find((item) => eq(item.value, countries));
            if (match) { filterArr.push(match.label); }
          }
          break;
        case 'topAuthorsOnly':
          if (topAuthorsOnly) {
            const match = f.filters.find((item) => eq(item.value, topAuthorsOnly));
            if (match) { filterArr.push(match.label); }
          }
          break;
        case 'topOutletsOnly':
          if (topOutletsOnly) {
            const match = f.filters.find((item) => eq(item.value, topOutletsOnly));
            if (match) { filterArr.push(match.label); }
          }
          break;
        case 'authorFollowSelected':
          if (authorFollowSelected) {
            const match = f.filters.find((item) => eq(item.value, authorFollowSelected));
            if (match) { filterArr.push(match.label); }
          }
          break;
        case 'outletFollowSelected':
          if (outletFollowSelected) {
            const match = f.filters.find((item) => eq(item.value, outletFollowSelected));
            if (match) { filterArr.push(match.label); }
          }
          break;
        case 'authors':
          if (!isEmpty(authors)) { filterArr.push(`Include ${authors.length} author${authors.length > 1 ? 's' : ''}`); }
          break;
        case 'outlets':
          if (!isEmpty(outlets)) { filterArr.push(`Include ${outlets.length} outlet${outlets.length > 1 ? 's' : ''}`); }
          break;
        case 'excludeAuthors':
          if (!isEmpty(excludeAuthors)) {
            filterArr.push(`Exclude ${excludeAuthors.length} author${excludeAuthors.length > 1 ? 's' : ''}`);
          }
          break;
        case 'excludeOutlets':
          if (!isEmpty(excludeOutlets)) {
            filterArr.push(`Exclude ${excludeOutlets.length} outlet${excludeOutlets.length > 1 ? 's' : ''}`);
          }
          break;
        case 'authorLabels':
          if (!isEmpty(authorLabels)) { filterArr.push(`Include ${authorLabels.length} author categor${authorLabels.length > 1 ? 'ies' : 'y'}`); }
          break;
        case 'excludeAuthorLabels':
          if (!isEmpty(excludeAuthorLabels)) { filterArr.push(`Exclude ${excludeAuthorLabels.length} author categor${excludeAuthorLabels.length > 1 ? 'ies' : 'y'}`); }
          break;
        case 'outletLabels':
          if (!isEmpty(outletLabels)) { filterArr.push(`Include ${outletLabels.length} outlet categor${outletLabels.length > 1 ? 'ies' : 'y'}`); }
          break;
        case 'excludeOutletLabels':
          if (!isEmpty(excludeOutletLabels)) { filterArr.push(`Exclude ${excludeOutletLabels.length} outlet categor${excludeOutletLabels.length > 1 ? 'ies' : 'y'}`); }
          break;
      }
    });
    return join(filterArr, ', ');
  }

  private buildSearchFilterFields(search: Search | null) {
    const defaultIgnoreLabelIds = search
      ? []
      : this.searchFilterDefaults.getIgnoreLabelIds();
    return {
      countries: search?.countries || null,
      outletGroups: search?.outletGroups || [],
      topAuthorsOnly: search?.topAuthorsOnly || false,
      topOutletsOnly: search?.topOutletsOnly || false,
      authorFollowSelected: search?.authorFollowSelected || null,
      outletFollowSelected: search?.outletFollowSelected || null,
      authors: search?.authors || [],
      excludeAuthors: search?.excludeAuthors || [],
      outlets: search?.outlets || [],
      excludeOutlets: search?.excludeOutlets || [],
      analysts: search?.analysts || null,
      authorLabels: search?.authorLabels || [],
      excludeAuthorLabels: search?.excludeAuthorLabels || defaultIgnoreLabelIds,
      outletLabels: search?.outletLabels || [],
      excludeOutletLabels: search?.excludeOutletLabels || defaultIgnoreLabelIds,
    };
  }

  private getActiveSearchFilterFields() {
    return this.buildSearchFilterFields(this.activeSearchFilters);
  }

  private hasSearchFilters(search: Search | null): boolean {
    if (!search) { return false; }
    const fields = this.buildSearchFilterFields(search);
    return !!(
      fields.countries ||
      fields.analysts ||
      fields.topAuthorsOnly ||
      fields.topOutletsOnly ||
      fields.authorFollowSelected ||
      fields.outletFollowSelected ||
      !isEmpty(fields.outletGroups) ||
      !isEmpty(fields.authors) ||
      !isEmpty(fields.excludeAuthors) ||
      !isEmpty(fields.outlets) ||
      !isEmpty(fields.excludeOutlets) ||
      !isEmpty(fields.authorLabels) ||
      !isEmpty(fields.excludeAuthorLabels) ||
      !isEmpty(fields.outletLabels) ||
      !isEmpty(fields.excludeOutletLabels)
    );
  }

  private normalizeSavedSearchProps(search: SearchProps | null): SearchProps | null {
    if (!search) { return null; }
    return {
      ...search,
      from: this.coerceSavedDate(search.from),
      to: this.coerceSavedDate(search.to),
    };
  }

  private coerceSavedDate(value: Date | string): Date {
    if (!value) { return null; }
    if (value instanceof Date) { return value; }
    return this.toLocalDate(value);
  }

  private coerceSavedTopicId(value: any): number | null {
    if (value === null || value === undefined || value === '') { return null; }
    const topicId = Number(value);
    return Number.isFinite(topicId) ? topicId : null;
  }

  // Make sure the persisted IDs don't reference a topic which is no longer in the project
  private sanitizeSavedTopicId(value: any): number | null {
    const topicId = this.coerceSavedTopicId(value);
    if (topicId === null) { return null; }
    if (!this.validUserTopicIds || !this.validUserTopicIds.size) {
      return topicId;
    }
    return this.validUserTopicIds.has(topicId) ? topicId : null;
  }
  private sanitizeSavedTopicIds(value: any): number[] {
    const values = Array.isArray(value)
      ? value
      : value !== null && value !== undefined && value !== ''
        ? [value]
        : [];
    return values
      .map((topicId) => this.sanitizeSavedTopicId(topicId))
      .filter((topicId): topicId is number => topicId !== null);
  }

  private getDefaultChartFilterValues() {
    return {
      title: '',
      timeframe: 'ONE_YEAR',
      interval: 'ONE_MONTH',
      dataType: 'ARTICLES',
      chartType: 'BAR',
      showPieChart: false,
      calendarYear: true,
      forecast: false,
      compareToPrior: false,
      includeOther: true,
      displayNames: true,
      displayValues: true,
      hideZeroValues: false,
      showIntersections: false,
      domainTopic: null,
      competitorTopics: [],
      outletGroups: [],
      fromDate: null,
      toDate: null,
    };
  }

  private getDefaultCoverageReportChart(): CoverageReportChart {
    return {
      dataType: 'ARTICLES',
      timeframe: 'ONE_YEAR',
      interval: 'ONE_MONTH',
      compareToPrior: false,
      domainTopic: null,
      myCompanyTopic: null,
      competitorTopics: [],
      includeOther: false,
      calendarYear: true,
      forecast: false,
    };
  }

  private initializeDefaultReport(reports: any[]) {
    if (reports.length > 0) {
      this.store.dispatch(actions.selectMyReport({ report: reports[0] }));
    } else {
      this.applyStandardChartPreset('MY_COVERAGE');
    }
  }

  private resetProjectReportState() {
    this.showSaveReportDialog = false;
    this.activeReportId = null;
    this.savedReports = [];
    // Topic options and the company query are owned by the topics subscription.
    // A report reset does not change topics, so that subscription will not re-emit.
    this.coverageData = [];
    this.coverageDataChart = null;
    this.chartOptions = null;
    this.pieChartOptions = null;
    this.coverageReportChart = this.getDefaultCoverageReportChart();
    this.coverageReportRequest = { timeframe: 'ONE_YEAR' };
    this.chartFiltersForm.reset(this.getDefaultChartFilterValues(), { emitEvent: false });
    this.showChartSettings = false;
    this.activeStandardChartPreset = null;
    this.chartSettingsSnapshot = null;
    this.chartSettingsApplied = false;
    this.chartClickFilter = null;
    this.selectedChartInterval = null;
    this.activeSearchFilters = null;
    this.showCoverageTable = false;
    this.showDateRangeDialog = false;
    this.editFromDate = null;
    this.editToDate = null;
    this.loading = false;
    this.loadingChart = false;
    this.pageSize = 25;
    this.page = 1;
    this.totalItems = 0;
    this.totalPages = 0;
    this.currentPage = 1;
    this._lastChartDataType = 'ARTICLES';
    this.savingAuthorId = null;
    this.savingOutletId = null;
    this.savingArticleId = null;
  }

  private getFirstTopicOptionValue(): number | null {
    const firstTopic = this.topicOptions.find((option) => option.value !== null);
    return firstTopic ? firstTopic.value : null;
  }

  private getAllCompetitorTopicValues(): number[] {
    return this.competitorOptions.map((option) => option.value);
  }

  private getStandardChartPresetValues(preset: StandardChartPreset) {
    if (preset === 'MY_COVERAGE') {
      return {
        dataType: 'ARTICLES',
        domainTopic: null,
        competitorTopics: [],
        includeOther: false,
        timeframe: 'TWO_YEARS',
        calendarYear: true,
        interval: 'ONE_MONTH',
        chartType: 'AREA',
        showPieChart: false,
        displayNames: false,
        displayValues: true,
        hideZeroValues: false,
        forecast: false,
      };
    }
    if (preset === 'MY_COMPETITORS') {
      return {
        dataType: 'ARTICLES',
        domainTopic: null,
        competitorTopics: this.getAllCompetitorTopicValues(),
        includeOther: false,
        timeframe: 'FIVE_YEARS',
        interval: 'ONE_YEAR',
        chartType: 'BAR',
        showPieChart: true,
        displayNames: true,
        hideZeroValues: false,
        displayValues: true,
        forecast: false,
      };
    }
    if (preset === 'SHARE_OF_VOICE') {
      return {
        dataType: 'AUTHORS',
        domainTopic: this.getFirstTopicOptionValue(),
        competitorTopics: [],
        includeOther: true,
        timeframe: 'FIVE_YEARS',
        interval: 'ONE_YEAR',
        calendarYear: true,
        chartType: 'BAR',
        showPieChart: true,
        displayNames: false,
        displayValues: true,
        hideZeroValues: false,
        forecast: false,
      };
    }
    return null;
  }

  isAuthorSaved(item: CoverageReport): boolean {
    return !!find(this.savedAuthors, (a) => eq(a.partyId, item.authorId));
  }

  isOutletSaved(item: CoverageReport): boolean {
    return !!find(this.savedOutlets, (o) => eq(o.partyId, item.outletId));
  }

  isArticleSaved(item: CoverageReport): boolean {
    return !!find(this.savedContentItems, (c) => eq(c.contentItemId, item.id));
  }

  onAuthorStarToggled(item: CoverageReport, currentActive: boolean): void {
    this.savingAuthorId = item.authorId;
    const userProject = { value: this.projectId };
    const author = { id: item.authorId, name: item.author };
    if (currentActive) {
      this.store.dispatch(actions.deleteAuthorToProject({ partyId: item.authorId, userProject, author }));
    } else {
      this.store.dispatch(actions.addAuthorToProject({ partyId: item.authorId, userProject, author }));
    }
  }

  onOutletStarToggled(item: CoverageReport, currentActive: boolean): void {
    this.savingOutletId = item.outletId;
    const userProject = { value: this.projectId };
    const outlet = { id: item.outletId, name: item.outlet };
    if (currentActive) {
      this.store.dispatch(actions.deleteOutletToProject({ partyId: item.outletId, userProject, outlet }));
    } else {
      this.store.dispatch(actions.addOutletToProject({ partyId: item.outletId, userProject, outlet }));
    }
  }

  onArticleStarToggled(item: CoverageReport, currentActive: boolean): void {
    this.savingArticleId = item.id;
    if (currentActive) {
      this.store.dispatch(actions.deleteContentItemToProject({ projectId: this.projectId, contentItemId: item.id }));
    } else {
      this.store.dispatch(actions.saveContentItemToProject({ projectId: this.projectId, contentItemId: item.id }));
    }
  }

  saveChart() {
    this.saveReportTitle = this.reportName || '';
    this.showSaveReportDialog = true;
  }

  confirmSaveReport() {
    const title = this.saveReportTitle.trim();
    if (!this.showSaveReportDialog || !title) { return; }

    this.showSaveReportDialog = false;
    const { chartType, showPieChart, displayNames, displayValues, hideZeroValues } = this.chartFiltersForm.value;
    const searchBody = this.activeSearchFilters ? { ...this.activeSearchFilters } : null;
    const competitorTopics = this.sanitizeSavedTopicIds(this.coverageReportChart.competitorTopics);

    const report: MyUserReport = {
      name: title,
      data: JSON.stringify({
        ...this.coverageReportChart,
        competitorTopics,
        title,
        chartType,
        showPieChart,
        displayNames,
        displayValues,
        hideZeroValues,
        coverageReportRequest: { ...this.coverageReportRequest },
        searchBody,
      }),
    };

    // A chart/report is normally saved as a new report. Before saving, check
    // whether an existing report in this project already uses the same name.
    // If one does, ask the user whether they want to overwrite it. On accept,
    // overwrite the existing report; on reject, do nothing (cancel).
    const trimmedTitle = (title || '').trim();
    const existingReport = this.savedReports.find(
      (r) => eq(toLower((r?.name || '').trim()), toLower(trimmedTitle))
    );
    if (existingReport) {
      this.confirmationService.confirm({
        message: 'There is already a report saved with this name, do you wish to overwrite it?',
        header: 'Report Already Exists',
        icon: 'pi pi-exclamation-triangle',
        key: 'unfollowConfirmation',
        accept: () => {
          this.applySavedReportTitle(title);
          this.store.dispatch(
            actions.updateMyReport({ projectId: this.projectId, reportId: existingReport.id, report })
          );
        },
        reject: () => {
          // User declined to overwrite; do nothing (cancel).
        },
      });
      return;
    }

    this.applySavedReportTitle(title);
    this.store.dispatch(actions.addMyReport({ projectId: this.projectId, report }));
  }

  private applySavedReportTitle(title: string) {
    this.chartFiltersForm.patchValue({ title }, { emitEvent: false });
    this.chartSettingsForm.patchValue({ title }, { emitEvent: false });
    this.onTitleBlur();
  }

  private applyReportConfig(report: any) {
    let config: any = {};
    try {
      config = JSON.parse(report.data);
    } catch (_) { return; }

    const {
      chartType = 'BAR',
      dataType = 'ARTICLES',
      timeframe = 'ONE_YEAR',
      interval = 'ONE_MONTH',
      calendarYear = true,
      forecast = false,
      compareToPrior = false,
      includeOther = false,
      domainTopic = null,
      myCompanyTopic = null,
      competitorTopics = [],
      showPieChart = false,
      displayNames = false,
      displayValues = true,
      hideZeroValues = false,
      showIntersections = false,
    } = config;
    const savedCoverageReportRequest = this.normalizeSavedSearchProps(config.coverageReportRequest);
    const savedSearchBody = this.normalizeSavedSearchProps(
      config.searchBody || savedCoverageReportRequest || config
    );
    const restoredSearchFilters = this.searchFilterDefaults.createSearch(savedSearchBody || {});
    const searchFilters = this.hasSearchFilters(restoredSearchFilters) ? restoredSearchFilters : null;
    const searchFilterFields = this.buildSearchFilterFields(searchFilters);
    const chartFrom = this.coerceSavedDate(config.from);
    const chartTo = this.coerceSavedDate(config.to);
    const savedCompetitorTopics = this.sanitizeSavedTopicIds(competitorTopics);

    this.applyingPreset = true;
    this.activeStandardChartPreset = null;
    this.collapseCoverageTable();
    this.chartClickFilter = null;
    this.activeSearchFilters = searchFilters;
    this.searchBody = searchFilters;

    this.chartFiltersForm.patchValue({
      title: report.name || '',
      chartType,
      dataType,
      timeframe,
      interval,
      calendarYear,
      forecast,
      compareToPrior,
      includeOther: this.sanitizeSavedTopicId(domainTopic) ? includeOther : false,
      domainTopic: this.sanitizeSavedTopicId(domainTopic),
      competitorTopics: savedCompetitorTopics,
      outletGroups: searchFilters?.outletGroups || [],
      fromDate: chartFrom,
      toDate: chartTo,
      showPieChart,
      displayNames,
      displayValues,
      hideZeroValues,
      showIntersections,
    }, { emitEvent: false });

    this._lastChartDataType = dataType;
    this.coverageReportChart = {
      ...this.coverageReportChart,
      dataType,
      timeframe,
      interval,
      calendarYear,
      forecast,
      compareToPrior,
      includeOther: this.sanitizeSavedTopicId(domainTopic) ? includeOther : false,
      domainTopic: this.sanitizeSavedTopicId(domainTopic),
      myCompanyTopic: this.sanitizeSavedTopicId(myCompanyTopic),
      showIntersections,
      competitorTopics: savedCompetitorTopics,
      from: chartFrom,
      to: chartTo,
      keywords: config.keywords || null,
      ...searchFilterFields,
    };

    const defaultCoverageReportRequest = {
      timeframe,
      from: chartFrom,
      to: chartTo,
      keywords: config.keywords || null,
      ...searchFilterFields,
    };
    this.coverageReportRequest = {
      ...defaultCoverageReportRequest,
      ...(savedCoverageReportRequest || {}),
      ...searchFilterFields,
      from: savedCoverageReportRequest?.from || defaultCoverageReportRequest.from,
      to: savedCoverageReportRequest?.to || defaultCoverageReportRequest.to,
    };

    if (this.chartOptions) {
      this.initializeChartOptions();
    }
    this.getCoverageDataChart();
    this.applyingPreset = false;
  }
}
